import clsx from 'clsx';
import { ReactNode } from 'react';
import { OctagonAlert, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardDescription,
  CardTitle,
} from '@/components/shared/shadcn-ui/card';
import { RestaurantShift } from '@durma-soft/gros-sdk/dist/types/restaurants';
import { TrackPickedUpOrder } from '@durma-soft/gros-sdk';

import { TrackPickedUpOrdersList } from '@/components/shared/track-picked-up-orders-list';
import { ChooseSubstituteMeal as CompanyChooseSubstituteMeal } from '../company/track/choose-substitute-meal';
import { ChooseSubstituteMeal as RestaurantChooseSubstituteMeal } from '../restaurant/track/choose-substitute-meal';
import { GiveDefaultSubstituteMeal as CompanyGiveDefaultSubstituteMeal } from '../company/track/give-default-substitute-meal';
import { GiveDefaultSubstituteMeal as RestaurantGiveDefaultSubstituteMeal } from '../restaurant/track/give-default-substitute-meal';

interface GenericTrackErrorProps {
  title?: ReactNode;
  description?: ReactNode;
  className?: string;
  onClose?: () => void;
  shift?: RestaurantShift;
  showSubstituteMeals?: boolean;
  selectionEnabled?: boolean;
  substituteAssigned?: boolean;
  substituteAlreadyAssigned?: boolean;
  /** Company owners and managers may take any number of substitute meals */
  unlimitedSubstituteMeals?: boolean;
  /** Whether the track location has a default substitute food configured */
  hasDefaultSubstituteFood?: boolean;
  substituteOrder?: TrackPickedUpOrder;
  pickedUpOrders?: TrackPickedUpOrder[];
  trackLocationId?: number;
  mode?: 'restaurant' | 'company';
  userId?: number;
  date?: string;
}

export const GenericTrackError = ({
  title,
  description,
  onClose,
  className,
  shift,
  showSubstituteMeals,
  selectionEnabled = true,
  substituteAssigned,
  substituteAlreadyAssigned = false,
  unlimitedSubstituteMeals = false,
  hasDefaultSubstituteFood = false,
  substituteOrder,
  pickedUpOrders,
  trackLocationId,
  mode = 'company',
  date,
  userId,
}: GenericTrackErrorProps) => {
  const { t } = useTranslation();

  const canCreateSubstitute =
    !!shift && !!date && !!userId && !!trackLocationId;

  // Only the first substitute is given automatically, further ones on request
  const canGiveDefaultSubstitute =
    unlimitedSubstituteMeals && hasDefaultSubstituteFood;

  return (
    <Card className={clsx('p-5 flex flex-col gap-4 relative', className)}>
      <OctagonAlert className="h-16 w-16 text-black" />
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      )}

      <CardTitle>{title}</CardTitle>
      <CardDescription>{description}</CardDescription>
      {!!pickedUpOrders?.length && (
        <TrackPickedUpOrdersList orders={pickedUpOrders} />
      )}
      {showSubstituteMeals && substituteAlreadyAssigned && (
        <>
          <CardDescription className="xl:text-2xl">
            {t('track.no-order.substitute-meal-already-assigned')}
          </CardDescription>
          {substituteOrder && (
            <TrackPickedUpOrdersList orders={[substituteOrder]} />
          )}
        </>
      )}
      {showSubstituteMeals &&
        !substituteAlreadyAssigned &&
        !selectionEnabled &&
        (substituteAssigned || !canGiveDefaultSubstitute) && (
          <CardDescription className="xl:text-2xl">
            {substituteAssigned
              ? t('track.no-order.substitute-meal-assigned')
              : t('track.no-order.no-substitute-meal-assigned')}
          </CardDescription>
        )}
      {showSubstituteMeals &&
        canGiveDefaultSubstitute &&
        !selectionEnabled &&
        canCreateSubstitute && (
          <>
            {mode === 'company' && (
              <CompanyGiveDefaultSubstituteMeal
                shift={shift}
                date={date}
                userId={userId}
                companyTrackLocationId={trackLocationId}
                onOrderCreated={onClose}
              />
            )}
            {mode === 'restaurant' && (
              <RestaurantGiveDefaultSubstituteMeal
                shift={shift}
                date={date}
                userId={userId}
                restaurantTrackLocationId={trackLocationId}
                onOrderCreated={onClose}
              />
            )}
          </>
        )}
      {showSubstituteMeals &&
        !substituteAlreadyAssigned &&
        selectionEnabled &&
        canCreateSubstitute && (
          <>
            {mode === 'company' && (
              <CompanyChooseSubstituteMeal
                shift={shift}
                date={date}
                userId={userId}
                companyTrackLocationId={trackLocationId}
                onOrderCreated={onClose}
              />
            )}
            {mode === 'restaurant' && (
              <RestaurantChooseSubstituteMeal
                shift={shift}
                date={date}
                userId={userId}
                restaurantTrackLocationId={trackLocationId}
                onOrderCreated={onClose}
              />
            )}
          </>
        )}
    </Card>
  );
};
